/**
 * Dashboard home surface (pirx-3apc9).
 *
 * Thin ScreenHome port: KPI cards, workflow launcher, and recent activity derived
 * client-side from GET /audits. `/` stays Upload; this lives at `/home`.
 */

import {
  BookOpen,
  FileText,
  History as HistoryIcon,
  LayoutDashboard,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Page } from "@/components/layout/Page";
import { PageHeader } from "@/components/layout/PageHeader";
import { SectionLabel } from "@/components/shared/SectionLabel";
import { ScoreRing } from "@/components/shared/ScoreRing";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertAction } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/useAuth";
import { useDashboardOverview } from "@/features/dashboard/useDashboardOverview";
import { ActivityRow } from "@/features/dashboard/components/ActivityRow";
import { StatCard } from "@/features/dashboard/components/StatCard";
import { WorkflowCard } from "@/features/dashboard/components/WorkflowCard";
import { complianceRingColor } from "@/features/dashboard/dashboard-metrics";
import { isFeatureEnabled, type FlagName } from "@/services/feature-flags";

type WorkflowDef = {
  id: string;
  route: string;
  icon: LucideIcon;
  accent: string;
  flag?: FlagName;
};

const WORKFLOWS: WorkflowDef[] = [
  {
    id: "audit",
    route: "/",
    icon: ShieldCheck,
    accent: "var(--brand)",
  },
  {
    id: "history",
    route: "/history",
    icon: HistoryIcon,
    accent: "var(--risk-med)",
  },
  {
    id: "monitoring",
    route: "/monitoring",
    icon: LayoutDashboard,
    accent: "var(--risk-low)",
    flag: "WEBSITE_MONITORING",
  },
  {
    id: "kb",
    route: "/knowledge-base",
    icon: BookOpen,
    accent: "var(--brand-ink)",
    flag: "KNOWLEDGE_BASE",
  },
];

export default function Dashboard() {
  const { t } = useTranslation(["dashboard", "common"]);
  const navigate = useNavigate();
  const { profile } = useAuth();
  const {
    auditsThisMonth,
    auditsLastMonth,
    openHighRisk,
    complianceScore,
    complianceTone,
    recentAudits,
    total,
    loading,
    error,
    refresh,
  } = useDashboardOverview({ t });

  const workflows = useMemo(
    () => WORKFLOWS.filter((workflow) => !workflow.flag || isFeatureEnabled(workflow.flag)),
    [],
  );

  const greetingName = profile?.name?.trim() || profile?.email?.split("@")[0] || t("dashboard:greeting.fallback");
  const kpiUnavailable = Boolean(error);

  if (loading) {
    return (
      <Page max={1140}>
        <div className="space-y-8" role="status" aria-label={t("dashboard:loading")}>
          <Skeleton className="h-8 w-3/4 max-w-xs" />
          <Skeleton className="h-4 w-1/2 max-w-md" />
          <div className="grid gap-3.5 md:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-3 rounded-[var(--r-lg)] border border-[var(--line)] bg-[var(--card)] p-[18px]">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-8 w-1/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
          <Skeleton className="h-6 w-1/3" />
          <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-[188px] rounded-[var(--r-lg)]" />
            ))}
          </div>
          <div className="rounded-[var(--r-lg)] border border-[var(--line)] bg-[var(--card)]">
            <div className="h-12 border-b border-[var(--line)]" />
            <div className="space-y-3 p-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          </div>
        </div>
      </Page>
    );
  }

  const monthDelta = kpiUnavailable ? 0 : auditsThisMonth - auditsLastMonth;
  const monthHint = kpiUnavailable
    ? t("dashboard:kpi.unavailable")
    : monthDelta === 0
      ? t("dashboard:kpi.auditsFlat")
      : t(monthDelta > 0 ? "dashboard:kpi.auditsUp" : "dashboard:kpi.auditsDown", {
          count: Math.abs(monthDelta),
        });
  const highRiskHint = kpiUnavailable
    ? t("dashboard:kpi.unavailable")
    : openHighRisk > 0
      ? t("dashboard:kpi.needsAttention")
      : t("dashboard:kpi.noHighRisk");
  const highRiskHintTone = kpiUnavailable ? "unavailable" : openHighRisk > 0 ? "high" : "low";
  const totalHint = kpiUnavailable ? t("dashboard:kpi.unavailable") : t("dashboard:kpi.totalHint");

  return (
    <Page max={1140}>
      <PageHeader
        eyebrow={(
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="size-3.5" aria-hidden="true" />
            {t("dashboard:eyebrow")}
          </span>
        )}
        title={t("dashboard:greeting.title", { name: greetingName })}
        sub={t("dashboard:greeting.subtitle")}
      />

      {error ? (
        <Alert variant="error" className="mb-6" data-test="dashboard-error">
          <AlertDescription className="flex-1">{error}</AlertDescription>
          <AlertAction>
            <Button variant="outline" size="sm" onClick={refresh} data-test="dashboard-error-retry">
              {t("common:buttons.retry")}
            </Button>
          </AlertAction>
        </Alert>
      ) : null}

      <div className="mb-7 grid gap-3.5 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t("dashboard:kpi.auditsThisMonth")}
          value={kpiUnavailable ? t("common:labels.none") : auditsThisMonth}
          hint={monthHint}
          hintTone={kpiUnavailable ? "unavailable" : "low"}
          icon={ShieldCheck}
          unavailable={kpiUnavailable}
        />
        <StatCard
          label={t("dashboard:kpi.openHighRisk")}
          value={kpiUnavailable ? t("common:labels.none") : openHighRisk}
          hint={highRiskHint}
          hintTone={highRiskHintTone}
          icon={ShieldAlert}
          unavailable={kpiUnavailable}
        />
        <StatCard
          label={t("dashboard:kpi.totalAudits")}
          value={kpiUnavailable ? t("common:labels.none") : total}
          hint={totalHint}
          hintTone={kpiUnavailable ? "unavailable" : "low"}
          icon={FileText}
          unavailable={kpiUnavailable}
        />
        <Card className="rounded-[var(--r-lg)] border-[var(--line)] bg-[var(--card)] shadow-none">
          <CardContent className="flex items-center gap-4 p-[18px]">
            <ScoreRing
              value={complianceScore ?? 0}
              label={t("dashboard:kpi.complianceLabel")}
              size={66}
              stroke={7}
              color={complianceRingColor(complianceTone)}
            />
            <div>
              <div className="text-[12.5px] font-semibold text-[var(--ink-3)]">
                {t("dashboard:kpi.complianceTitle")}
              </div>
              <p className="mt-1 text-[13px] leading-snug text-[var(--ink-2)]">
                {kpiUnavailable
                  ? t("dashboard:kpi.complianceUnavailable")
                  : complianceScore === null
                    ? t("dashboard:kpi.complianceEmpty")
                    : t(`dashboard:kpi.complianceTone.${complianceTone}`)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <SectionLabel className="mb-3.5">{t("dashboard:workflows.title")}</SectionLabel>
      <div className="mb-8 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
        {workflows.map((workflow) => (
          <WorkflowCard
            key={workflow.id}
            title={t(`dashboard:workflows.${workflow.id}.title`)}
            description={t(`dashboard:workflows.${workflow.id}.description`)}
            tagline={t(`dashboard:workflows.${workflow.id}.cta`)}
            accent={workflow.accent}
            icon={workflow.icon}
            onClick={() => void navigate(workflow.route)}
          />
        ))}
      </div>

      <Card className="overflow-hidden rounded-[var(--r-lg)] border-[var(--line)] bg-[var(--card)] shadow-none">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-5 pt-4 pb-3">
            <SectionLabel>{t("dashboard:activity.title")}</SectionLabel>
            <Button variant="link" size="sm" onClick={() => void navigate("/history")}>
              {t("dashboard:activity.viewAll")}
            </Button>
          </div>
          <div className="px-3 pb-2" data-test="dashboard-activity-list">
            {recentAudits.length === 0 ? (
              <p className="px-2 py-6 text-sm text-[var(--ink-3)]">{t("dashboard:activity.empty")}</p>
            ) : (
              recentAudits.map((audit) => (
                <ActivityRow
                  key={audit.id}
                  audit={audit}
                  onClick={() => void navigate(`/results/${audit.id}`)}
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}